import { AssistantMessage, ChatLog, Composer, ErrorNotice, UserBubble, answerWorkflowStep, useWorkflowComposer, workflowReplyComposer, workflowReplyIssue } from "@sagot/chat";
import { useTranslation } from "@sagot/i18n";
import { Icon } from "@sagot/ui";
import { useEffect, useState } from "react";
import { useStageLabels } from "../../../build/playground/chat-pane";
import { usePlaygroundChat, type PlaygroundSeed } from "../../../build/playground/use-playground-chat";
import { useWorkflowCopy } from "../../../build/playground/workflow-copy";
import { useWorkflowRun } from "../use-workflows";
import { SimulateOutcome } from "./simulate-outcome";

const P = "engage.workflows.builder.preview";
/** The Playground's "everyone" access preset: what an anonymous visitor reads. */
const ANONYMOUS_AUDIENCE = 1;

interface PreviewChatProps {
  workflowId: string;
  agentId: string;
  agentName: string;
  /** The preview conversation and what the workflow already said in it. */
  seed: PlaygroundSeed;
  /** The trigger starts from the tester's first message. */
  awaitingMessage: boolean;
  /** The run started but no workflow lines came back (its settings did not match). */
  notStarted: boolean;
}

/** The test conversation: the real workflow cards, then the assistant's usual answers. */
export function PreviewChat({ workflowId, agentId, agentName, seed, awaitingMessage, notStarted }: PreviewChatProps) {
  const { t } = useTranslation();
  const chat = usePlaygroundChat(agentId, ANONYMOUS_AUDIENCE, seed);
  const stageLabels = useStageLabels();
  const workflowLabels = useWorkflowCopy();
  const workflowComposer = useWorkflowComposer(chat.messages);
  const replyCard = workflowComposer.mode === "reply" ? workflowComposer.card : undefined;
  /** Why the composer holds back the text typed for the open question (a mistyped code); a preview has no bot check. */
  const [replyIssue, setReplyIssue] = useState<string | null>(null);
  /** The composer answers the open question when it asks for one, else it sends a message. */
  const submit = (text: string) => {
    if (!replyCard) {
      chat.send(text);
      return;
    }
    if (answerWorkflowStep(chat.send, replyCard, { value: text })) {
      setReplyIssue(null);
      return;
    }
    const issue = workflowReplyIssue(replyCard, text, workflowLabels);
    setReplyIssue(issue);
    if (issue === null) chat.send(text); // not a code: the server asks again (five of them end the step)
  };
  const runId = chat.messages.findLast((message) => message.workflowRunId !== undefined)?.workflowRunId ?? null;
  const run = useWorkflowRun(workflowId, runId);
  const { refetch } = run;
  // The path the run has taken so far, read again whenever an answer has finished.
  useEffect(() => {
    if (runId !== null && !chat.isBusy) void refetch();
  }, [runId, chat.isBusy, chat.messages.length, refetch]);
  const firstAnswer = chat.messages.find((message) => message.role === "assistant" && message.status === "done");
  // A run waits for the assistant only at "Let the assistant answer": the author may choose how that answer goes.
  const conversationId = chat.conversationId;
  const outcomeTarget = runId !== null && conversationId !== null && !chat.isBusy && run.data?.status === "delegated" ? { runId, conversationId } : null;
  const silent = awaitingMessage && firstAnswer !== undefined && firstAnswer.workflowRunId === undefined;
  const hint = awaitingMessage && chat.messages.length === 0 ? t(`${P}.waitsForMessage`) : notStarted ? t(`${P}.notStarted`) : silent ? t(`${P}.noRun`) : null;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <ChatLog busy={chat.isBusy} label={t(`${P}.conversation`)} className="min-h-72 rounded-lg bg-sunken">
        <div className="space-y-4 p-3">
          {hint ? (
            <p className="flex items-start gap-2 rounded-md bg-surface p-3 text-xs text-fg-muted" data-testid="preview-hint">
              <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
              {hint}
            </p>
          ) : null}
          {chat.messages.map((message) =>
            message.role === "user" ? (
              <UserBubble key={message.id} text={message.text} />
            ) : (
              <AssistantMessage
                key={message.id}
                message={message}
                botName={agentName}
                onAsk={chat.send}
                audience="staff"
                stageLabels={stageLabels}
                relatedLabel={t("chat.related")}
                busy={chat.isBusy}
                detailedSources
                previewActions
                workflowLabels={workflowLabels}
                onWorkflowAnswer={(card, choice) => { answerWorkflowStep(chat.send, card, choice, workflowLabels.skip); }}
              />
            ),
          )}
          <ErrorNotice error={chat.error} onRetry={chat.retry} labels={{ retry: t("build.playground.chat.retry") }} />
        </div>
      </ChatLog>
      {outcomeTarget ? (
        <SimulateOutcome
          key={outcomeTarget.runId}
          workflowId={workflowId}
          conversationId={outcomeTarget.conversationId}
          runId={outcomeTarget.runId}
          onMessages={(messages) => { for (const message of messages) chat.append(message); }}
        />
      ) : null}
      <Composer
        variant="compact"
        onSend={submit}
        onStop={chat.stop}
        streaming={chat.isBusy}
        lockedHint={workflowComposer.mode === "locked" ? workflowLabels.typingLocked : undefined}
        reply={replyCard ? { ...workflowReplyComposer(replyCard, workflowLabels), ...(replyIssue ? { error: replyIssue } : {}) } : undefined}
        onValueChange={(next) => { if (next) setReplyIssue(null); }}
        labels={{ input: t("chat.messageLabel"), send: t("chat.send"), hintSend: t("chat.enterToSend"), hintNewline: t("chat.newLine"), placeholder: t(`${P}.placeholder`), stop: t("build.playground.chat.stop") }}
      />
      {run.data ? <p className="text-center text-xs text-fg-subtle" data-testid="preview-path">{t(`${P}.pathTaken`, { path: run.data.path })}</p> : null}
      <p className="text-center text-xs text-fg-subtle">{t(`${P}.footer`)}</p>
    </div>
  );
}
